import { onReady } from './util/onReady';
import { createI18n, common } from '@bino/utils/i18next';

const i18n = createI18n(common);

type Article = {
    title: string;
    label: string;
    leadText: string;
    link: string;
    imageUrl: string;
    imageAlternateText: string;
    published: string;
    authors: string[];
};

type Publication = Article;

async function loadMore(loadMoreButton: HTMLElement): Promise<{
    results: (Article | Publication)[];
    numberToLoad: number;
}> {
    const numberToLoad = 12;
    const offset = Number(loadMoreButton.dataset.offset || 0);

    const urlParams = new URLSearchParams({
        size: String(numberToLoad),
        type: loadMoreButton.dataset.type || '',
        id: loadMoreButton.dataset.id || '',
        offset: String(offset),
    });
    const url = `/api/articles/topic?${urlParams.toString()}`;

    loadMoreButton.dataset.offset = String(offset + numberToLoad);

    try {
        const response = await fetch(url);
        const results = (await response.json()) as (Article | Publication)[];
        return { results, numberToLoad };
    } catch (e) {
        console.error('parsing failed', e);
        return { results: [], numberToLoad };
    }
}

function toggleSpinner(loadMoreButton: HTMLElement, spinnerElement: HTMLElement | null) {
    spinnerElement?.classList.toggle('show');
    loadMoreButton.classList.toggle('disabled');
}

function renderArticle({ link, imageUrl, imageAlternateText, label, title, leadText }: Article) {
    return `<bi-article
    header="${title}"
    url="${link}"
    subtitle="${label}"
    body="${leadText}"
    image-url="${imageUrl}"
    image-alt-text="${imageAlternateText}">
</bi-article>`;
}

function renderPublication({ link, imageUrl, imageAlternateText, title, authors }: Publication) {
    return `<bi-article
    header="${title}"
    url="${link}"
    subtitle="${(authors || []).join(', ')}"
    body=""
    image-url="${imageUrl}"
    image-alt-text="${imageAlternateText}">
</bi-article>`;
}

function renderArticles<T extends Article | Publication>(articles: T[], templateFn: (article: T) => string) {
    return articles.map((article) => templateFn(article)).join('');
}

function noMoreArticles(container: HTMLElement) {
    container.insertAdjacentHTML('afterend', `<p class="no-more">${i18n.t('articles.noMore')}</p>`);
}

function hideLoadMore(loadMoreButton: HTMLElement) {
    if (loadMoreButton.parentElement) {
        loadMoreButton.parentElement.style.display = 'none';
    }
}

onReady(() => {
    const loadMoreButton = document.getElementById('js-load-more');
    if (loadMoreButton !== null) {
        loadMoreButton.addEventListener('click', async (ev) => {
            ev.preventDefault();
            const spinnerElement = document.querySelector<HTMLElement>('.spinner-block');

            toggleSpinner(loadMoreButton, spinnerElement);
            const template = loadMoreButton.dataset.template;
            const containerSelector = loadMoreButton.dataset.container;
            if (!containerSelector) return;
            const container = document.querySelector<HTMLElement>(containerSelector);
            if (!container) return;

            const { results, numberToLoad } = await loadMore(loadMoreButton);

            toggleSpinner(loadMoreButton, spinnerElement);

            let html = '';
            if (template === undefined || template === 'businessreview' || template === 'person') {
                html = renderArticles(results, renderArticle);
            } else if (template === 'publication') {
                html = renderArticles(results, renderPublication);
            }

            container.insertAdjacentHTML('beforeend', html);

            if (results.length < numberToLoad) {
                hideLoadMore(loadMoreButton);
            }
            if (results.length === 0) {
                noMoreArticles(container);
            }
        });
    }
});
